import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "@/packages/utils";
import { AuthLayout } from "./AuthLayout";
import { OtpVerificationForm } from "./OtpVerificationForm";
import { SignInForm } from "./SignInForm";
import { SignUpForm } from "./SignUpForm";

type AuthFlow = "signIn" | "signUp" | "otp-verification";

export interface AuthPageProps {
  flow?: AuthFlow;
  onFlowChange?: (flow: AuthFlow) => void;
  brandName?: string;
  onSignInSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  signInIsLoading?: boolean;
  signInError?: string | null;
  onSignUpSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  signUpPassword: string;
  onSignUpPasswordChange: (value: string) => void;
  signUpIsLoading?: boolean;
  signUpError?: string | null;
  signUpHasAttemptedSubmit?: boolean;
  // OTP verification props
  onOtpSubmit?: (code: string) => void;
  onOtpResend?: () => void;
  onOtpBack?: () => void;
  otpIsLoading?: boolean;
  otpError?: string | null;
  verificationEmail?: string | null;
}

const PANEL_CONTENT: Record<AuthFlow, { headline: string; subline: string }> = {
  signIn: {
    headline: "Welcome back",
    subline:
      "Sign in to continue managing your audit preparation and compliance tracking.",
  },
  signUp: {
    headline: "Start your journey",
    subline:
      "Create an account to streamline audit readiness with AI-powered document management.",
  },
  "otp-verification": {
    headline: "Almost there",
    subline:
      "We sent a verification code to confirm your identity. Check your inbox.",
  },
};

const formTransition = {
  enter: { opacity: 0, x: 10 },
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
  exit: {
    opacity: 0,
    x: -10,
    transition: { duration: 0.18 },
  },
};

export function AuthPage({
  flow = "signIn",
  onFlowChange,
  onSignInSubmit,
  signInIsLoading,
  signInError,
  onSignUpSubmit,
  signUpPassword,
  onSignUpPasswordChange,
  signUpIsLoading,
  signUpError,
  signUpHasAttemptedSubmit,
  onOtpSubmit,
  onOtpResend,
  onOtpBack,
  otpIsLoading,
  otpError,
  verificationEmail,
}: AuthPageProps) {
  const { t: _t } = useTranslation(["auth"]);
  const [internalFlow, setInternalFlow] = useState<AuthFlow>(flow);
  const currentFlow = onFlowChange ? flow : internalFlow;

  const handleFlowToggle = () => {
    const newFlow = currentFlow === "signIn" ? "signUp" : "signIn";
    if (onFlowChange) {
      onFlowChange(newFlow);
    } else {
      setInternalFlow(newFlow);
    }
  };

  const { headline, subline } = PANEL_CONTENT[currentFlow];

  return (
    <AuthLayout headline={headline} subline={subline}>
      <AnimatePresence mode="wait">
        {currentFlow === "otp-verification" &&
        onOtpSubmit &&
        onOtpResend &&
        onOtpBack &&
        verificationEmail ? (
          <motion.div
            key="otp"
            variants={formTransition}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <OtpVerificationForm
              email={verificationEmail}
              onSubmit={onOtpSubmit}
              onResend={onOtpResend}
              onBack={onOtpBack}
              isLoading={otpIsLoading}
              error={otpError}
            />
          </motion.div>
        ) : currentFlow === "signUp" ? (
          <motion.div
            key="signup"
            variants={formTransition}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <SignUpForm
              onSubmit={onSignUpSubmit}
              password={signUpPassword}
              onPasswordChange={onSignUpPasswordChange}
              isLoading={signUpIsLoading}
              error={signUpError}
              hasAttemptedSubmit={signUpHasAttemptedSubmit}
              onToggleFlow={handleFlowToggle}
            />
          </motion.div>
        ) : (
          <motion.div
            key="signin"
            variants={formTransition}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <SignInForm
              onSubmit={onSignInSubmit}
              isLoading={signInIsLoading}
              error={signInError}
              onToggleFlow={handleFlowToggle}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </AuthLayout>
  );
}
